/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMDraftEndpointBasicsComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorPanelUtils, cloneEndpoint } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { InputDataType } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";

import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { Checkbox } from "VSSUI/Checkbox";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { InputDetails } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { EndpointEditorBaseComponent, IEndpointEditorBaseComponentState, IEndpointEditorBaseComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointEditorBaseComponent";

export interface IAzureRMDraftEndpointBasicsComponent extends IEndpointEditorBaseComponentProps {
    updateValidAndDirtyStateChange: (isValid?: boolean, isDirty?: boolean) => void;
    hideHeader?: boolean;
    propsOnNameInput?: any;
    propsOnDescriptionInput?: any;
    warnOnNameChange?: boolean;
    hasNameChanged?: boolean;
}

export class AzureRMDraftEndpointBasicsComponent extends EndpointEditorBaseComponent<IAzureRMDraftEndpointBasicsComponent, IEndpointEditorBaseComponentState>{
    constructor(props: IAzureRMDraftEndpointBasicsComponent, context: IVssComponentContext) {
        super(props, context);
        this._draftServiceEndpoint = cloneEndpoint(props.draftEndpoint);

        this._inputDetailsList = [
            {
                inputId: this._nameInputId,
                initialValue: props.draftEndpoint.name,
                inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true),
                isConfidential: false,
                isUpdate: this.props.isUpdateForm
            },
            {
                inputId: this._descriptionInputId,
                initialValue: props.draftEndpoint.description,
                inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, false),
                isConfidential: false,
                isUpdate: this.props.isUpdateForm
            }
        ];
    }

    public componentDidMount() {
        this.addInputs(this._inputDetailsList);
    }

    public componentWillReceiveProps(props: IAzureRMDraftEndpointBasicsComponent) {
        this._draftServiceEndpoint = cloneEndpoint(props.draftEndpoint);
    }

    public componentWillUnmount() {
        let currentInputIds: string[] = [];
        this._inputDetailsList.forEach((input) => currentInputIds.push(input.inputId));
        this.removeInputs(currentInputIds);
    }

    public render(): React.ReactNode {
        const {
            hideHeader = false,
            propsOnNameInput = {},
            propsOnDescriptionInput = {},
            warnOnNameChange = false,
        } = this.props;
        return (
            <div className="service-connection-details-section">
                {!hideHeader && <span role="heading" aria-level={4} className="service-endpoint-section-headers">{Resources.DetailsHeader}</span>}
                <FormItem
                    label={Resources.NameInputLabel}
                    className="default-input-margin-top"
                    message={this._getErrorMessage(this._nameInputId)}
                    error={this._hasError(this._nameInputId)}
                >
                    <TextField
                        {...propsOnNameInput}
                        value={this._draftServiceEndpoint.name}
                        onChange={this._onNameChange}
                    />
                </FormItem>

                {warnOnNameChange && this._renderNameChangePanel()}

                <FormItem
                    label={Resources.DescriptionInputLabel}
                    className="default-input-margin-top"
                    message={this._getErrorMessage(this._descriptionInputId)}
                    error={this._hasError(this._descriptionInputId)}
                >
                    <TextField
                        {...propsOnDescriptionInput}
                        value={this._draftServiceEndpoint.description}
                        onChange={this._onDescriptionChange}
                        multiline={true}
                    />
                </FormItem>
                <div className="pipeline-permissions-section default-input-margin-top">
                    <div className="bolt-formitem-label body-m">{Resources.SecurityHeader}</div>
                    <Checkbox
                        onChange={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => {
                            this.props.updateAllowAllPipelinesFlag(checked);
                        }}
                        checked={this.props.allowAllPipelinesFlagState}
                        label={Resources.PipelinePermissionsCheckboxLabel}
                        disabled={this.props.disableAllPipelinesSetting}
                    />
                </div>
            </div>
        );
    }

    private _renderNameChangePanel = () => {
        const { hasNameChanged = false } = this.props;
        if (hasNameChanged) {
            return <React.Fragment>
                <div className="padding-top-4 padding-bottom-16">
                    <span className="name-changes-warning-title">{Resources.Warning}:&nbsp;</span>
                    <span className="name-changes-warning-description">{Resources.WorkloadIdentityFederationNameChangeWarningDescription}&nbsp;</span>
                    <Link target="_blank" href="https://aka.ms/azdo-rm-workload-identity-guidance/">{Resources.LearnMoreText}</Link>
                </div>
            </React.Fragment>;
        } else {
            return null;
        }
    }

    private _hasError(inputId: string): boolean {
        return (!!this.getInputError(inputId));
    }

    private _getErrorMessage(inputId: string): string {
        return this.getInputError(inputId) || "";
    }

    private _onNameChange = (event: any, value: string) => {
        this._draftServiceEndpoint.name = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._nameInputId, value);
    }

    private _onDescriptionChange = (event: any, value: string) => {
        this._draftServiceEndpoint.description = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._descriptionInputId, value);
    }

    protected handleEnableSaveStateChange(enableSave: boolean) {
        this.props.updateInvalidInputsCount(enableSave ? -1 : 1);
    }

    protected handleValidAndDirtyStateChange(isValid?: boolean, isDirty?: boolean) {
        this.props.updateValidAndDirtyStateChange(isValid, isDirty);
    }

    private _draftServiceEndpoint: ServiceEndpoint;
    private _nameInputId = "endpoint-name";
    private _descriptionInputId = "endpoint-description";
    private _inputDetailsList: InputDetails[];
}
